import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import Text from '../components/elements/TextComponent';
import Button from '../components/elements/Button';
import CircularImageComponent from '../components/elements/CircularImageComponent';
import TextTyper from '../components/static/TextTyper';
import HighlightsComponent from '../components/static/HighlightsComponent';
import ChatInput from '../components/static/ChatInput';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { chatWithBot, createThreadWithBot, getBotDetails } from '../api/assistantsApi';
import { CreateThreadResponse } from '../api/types';
import axiosInstance from '../api/axiosInstance';

interface Props {}

/* --------------------- ConsentModal (Portal) --------------------- */
interface ConsentModalProps {
  open: boolean;
  onAccept: () => void;
}

const ConsentModal: React.FC<ConsentModalProps> = ({ open, onAccept }) => {
  if (!open) return null;

  return ReactDOM.createPortal(
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.6 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: '#000',
          zIndex: 1000,
        }}
      />

      {/* Modal */}
      <motion.div
        initial={{ opacity: 0, y: 12, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -8, scale: 0.98 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="chat-consent-title"
        style={{
          position: 'absolute',
          top: '40%',
          left: 'calc(65% - min(560px, 92vw))',
          // transform: 'translate(-50%, -50%)',
          width: 'min(560px, 92vw)',
          backgroundColor: '#fff',
          borderRadius: 16,
          boxShadow: '0 20px 48px rgba(0,0,0,0.25)',
          padding: 24,
          zIndex: 1001,
        }}
      >
        <Text
          text="Before you continue"
          fontSize="22px"
          fontWeight="700"
          lineHeight="28px"
          color="#030303"
          textAlign="left"
        />
        <div style={{ marginTop: 12 }}>
          <Text
            text="By using this chatbot, your messages and the time you send them will be recorded and visible to the bot owner."
            fontSize="16px"
            fontWeight="400"
            lineHeight="24px"
            color="#030303"
            textAlign="left"
          />
        </div>
        <div style={{ marginTop: 8 }}>
          <Text
            text="See our Terms of Service and Privacy Policy for details."
            fontSize="14px"
            fontWeight="400"
            lineHeight="20px"
            color="#4a4a4a"
            textAlign="left"
          />
        </div>

        <div style={{ marginTop: 16, display: 'flex', gap: 12 }}>
          <a href="/termsofservice" style={{ color: 'var(--primary)', textDecoration: 'none' }}>
            Terms of Service
          </a>
          <span style={{ color: '#9a9a9a' }}>•</span>
          <a href="/privacypolicy" style={{ color: 'var(--primary)', textDecoration: 'none' }}>
            Privacy Policy
          </a>
        </div>

        <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
          <Button
            backgroundColor="#eaeaea"
            color="#030303"
            label="Cancel"
            onClick={() => {}}
          />
          <Button
            backgroundColor="var(--primary)"
            color="#ffffff"
            label="Agree & Continue"
            onClick={onAccept}
          />
        </div>
      </motion.div>
    </>,
    document.body
  );
};

/* --------------------- Chat Screen --------------------- */

const Chat: React.FC<Props> = () => {
  const { botSlug } = useParams<{ botSlug: string }>();
  const botId = botSlug?.split('-')[0];
  // const CONSENT_KEY = botId ? `ccwm_chat_consent_${botId}` : 'ccwm_chat_consent';

  const [name, setName] = useState('');
  const [botEnabled, setBotEnabled] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [keywords, setKeywords] = useState<string[]>([]);
  const [location, setLocation] = useState('');
  const [languages, setLanguages] = useState<string[]>([]);
  const [experiences, setExperiences] = useState<any[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loading, setLoading] = useState(true);

  const [threadId, setThreadId] = useState<string | null>(null);
  const [messages, setMessages] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [showConsent, setShowConsent] = useState<boolean>(false);

  // const allQuestions = [
  //   'What technologies do you work with?',
  //   'Tell me about your frontend experience.',
  //   'Have you worked with cloud platforms?',
  //   'How do you handle testing?',
  //   'What backend frameworks do you prefer?',
  //   'How do you ensure accessibility?',
  // ];
  // const [questionQueue, setQuestionQueue] = useState(allQuestions.slice(3));
  // const [visibleQuestions, setVisibleQuestions] = useState(allQuestions.slice(0, 2));
  
  const [questionQueue, setQuestionQueue] = useState<string[]>([]);
  const [visibleQuestions, setVisibleQuestions] = useState<string[]>([]);
  const [fadeStates, setFadeStates] = useState([true, true, true]);

  useEffect(() => {
    const fetchDetails = async () => {
      if (!botId) return;
      const { data, error } = await getBotDetails(botId);
      if (error) {
      } else if (data) {
        setName(data.name || '');
        setBotEnabled(Boolean(data.enabled));
        setImageUrl(data.metadata.profileImage || '');
        setKeywords(data.metadata.keywords || []);
        setLocation(data.metadata.location || '');
        setLanguages(data.metadata.spokenLanguages || []);
        setExperiences(data.metadata.experiences || []);
        setQuestionQueue(data.metadata.questions.slice(3) || [])
        setVisibleQuestions(data.metadata.questions.slice(0, 2) || [])
      }
      setLoading(false);
    };
    fetchDetails();
  }, [botId]);

  useEffect(() => {
    if (!botId) return;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const accepted = localStorage.getItem(botId ? `ccwm_chat_consent_${botId}` : 'ccwm_chat_consent') === '1';
    setShowConsent(!accepted);
  }, [botId]);

  const handleSend = async (msg: string) => {
    if (showConsent) return;
    if (!botId || !msg.trim()) return;

    setMessages((prev) => [...prev, `user_agent:${msg}`]);
    setIsLoading(true);
    removeAndReplace(msg);

    try {
      let data: CreateThreadResponse | null = null;
      if (!threadId) {
        const result = await createThreadWithBot(botId, msg);
        data = result.data;
      } else {
        const result = await chatWithBot(botId, msg, threadId);
        data = result.data;
      }
      if (!data) return;

      setThreadId(data.threadId);
      setMessages((prev) => [...prev, data?.message ?? '']);
    } catch (err) {
    } finally {
      setIsLoading(false);
    }
  };

  const removeAndReplace = (clickedQuestion: string) => {
    const clickedIndex = visibleQuestions.indexOf(clickedQuestion);
    if (clickedIndex === -1) return;
    const newFades = [...fadeStates];
    newFades[clickedIndex] = false;
    setFadeStates(newFades);

    setTimeout(() => {
      const updatedVisible = [...visibleQuestions];
      const newQueue = questionQueue.filter((q) => q !== clickedQuestion);
      const next = newQueue.find(q => !updatedVisible.includes(q));
      if (next) {
        updatedVisible[clickedIndex] = next;
      } else {
        updatedVisible.splice(clickedIndex, 1);
      }
      setQuestionQueue(newQueue);
      const updatedFades = [...fadeStates];
      if (next) {
        updatedFades[clickedIndex] = true;
      } else {
        updatedFades.splice(clickedIndex, 1);
      }
      setVisibleQuestions(updatedVisible);
      setFadeStates(updatedFades);
    }, 200);
  };

  useEffect(() => {
    if (!name) return;
    const timeout = setTimeout(() => {
      setMessages((prev) => [
        ...prev,
        `Hello, my name is ${name}! Feel free to ask questions on my professional experience`,
      ]);
    }, 1000);
    return () => clearTimeout(timeout);
  }, [name]);

  // if (loading) return <div>Loading bot...</div>;

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -20, scale: 0.98 }}
        transition={{ duration: 0.35, ease: 'easeInOut' }}
      > 
      {!botEnabled && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(255,255,255,0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
            padding: 24,
            textAlign: 'center',
          }}
        >
          <div style={{ maxWidth: 520 }}>
            <p style={{ fontFamily: 'Montserrat', fontSize: 18, fontWeight: 600, color: '#030303' }}>
              This assistant is currently unavailable.
            </p>
            <p style={{ marginTop: 8, color: '#555' }}>
              Access has expired.
            </p>
          </div>
        </div>
      )}
        <div className="split-screen">
          {/* LEFT SIDE */}
          <div className="left-side color-purple-background">
            <div className='margin-top-5p' style={{ width: '50vw', height: '100vh'}}>
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <CircularImageComponent src={imageUrl} width="300px" center />
              </div>
              <div className='margin-top-30'>
                <Text
                  text={name}
                  fontSize="32px"
                  fontWeight="700"
                  lineHeight="38px"
                  color="#ffffff"
                  textAlign="center"
                />
              </div>
              <div className='margin-top-20'>
                <Text
                  text={`This AI Assistant is answering as ${name} during an interview`}
                  fontSize="16px"
                  fontWeight="700"
                  lineHeight="28px"
                  color="#ffffff"
                  textAlign="center"
                />
              </div>
              <div className='margin-top-30'>
                <TextTyper
                  isLoading={isLoading}
                  messages={messages}
                  typingSpeed={4}
                  centered
                  width="80%"
                  height="400px"
                  fontSize="16px"
                  fontWeight="500"
                  lineHeight="28px"
                  color="white"
                  textAlign="left"
                />
              </div>
            </div>
          </div>

          {/* RIGHT SIDE */}
          <div className="right-side">
            <div className='margin-top-64 margin-right-64 margin-bottom-20'>
              <Text
                text="ComeChatWithMe.ai"
                fontSize="32px"
                fontWeight="700"
                lineHeight="38px"
                color="#030303"
                textAlign="right"
                navigateToHome
              />
              <Text
                text="Resume"
                fontSize="16px"
                fontWeight="400"
                lineHeight="18px"
                color="#030303"
                textAlign="right"
              />
            </div>

            <HighlightsComponent
              location={location}
              spokenLanguages={languages}
              keywords={keywords}
              experiences={experiences}
            />

            {/* Suggested Questions */}
            <div
              style={{
                position: 'relative',
                zIndex: 15,
                display: 'flex',
                gap: '12px',
                flexWrap: 'wrap',
                justifyContent: 'center',
                marginBottom: '20px',
                top: '30px',
              }}
            >
              {visibleQuestions.map((question, i) => (
                <button
                  key={question}
                  onClick={() => handleSend(question)}
                  className={`fade-in-out ${fadeStates[i] ? 'fade-in' : 'fade-out'}`}
                  style={{
                    fontFamily: 'Montserrat',
                    fontSize: '16px',
                    backgroundColor: '#f5f5f5',
                    border: '1px solid #dcdcdc',
                    borderRadius: '20px',
                    padding: '10px 16px',
                    cursor: 'pointer',
                  }}
                >
                  {question}
                </button>
              ))}
            </div>

            <div className='margin-top-192 margin-left-20p'>
              <div
                style={{
                  position: 'absolute',
                  bottom: 0,
                  right: "20px",
                  width: 'calc(50% - 40px)',
                  padding: '12px 16px',
                  backgroundColor: 'white',
                  borderRadius: '20px',
                  marginBottom: '20px',
                  boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.05)',
                  zIndex: 10,
                }}
              >
                <ChatInput onSend={handleSend} disabled={isLoading || showConsent || !botEnabled} />
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Consent Modal */}
      <ConsentModal
        open={showConsent}
        onAccept={async () => {
          localStorage.setItem(botId ? `ccwm_chat_consent_${botId}` : 'ccwm_chat_consent', '1');
          setShowConsent(false);
          // increment visitor count
          try {
            await axiosInstance.post('/bot/visit', {
            botId: botId
          }); 
          } catch (error) {
          }
        }}
      />
    </>
  );
};

export default Chat;
